import i18n from "i18next";
import { batch } from "react-redux";
import * as userApi from "../api/user";
import {
  buildAzureAdLogoutRedirectUrl,
  clearPersistedAuthMethod,
  persistOidcAuthMethod,
  preserveAuthMethodDuringStoreReset,
  restoreAuthMethodAfterStoreReset,
} from "../utils/azureAdLogout";
import { notify } from "./notification";
import { setEonsConfig } from "./eons";
import { setWalletConfig } from "./wallet";

export const SET_USER = "SET_USER";
export const RESET_STORE = "RESET_STORE";

export function getMe({ silent = false } = {}) {
  return async (dispatch, getState) => {
    let user;
    try {
      user = await userApi.getMe({ silent });
    } catch (err) {
      const status = err.response?.status;
      if (silent && (status === 401 || status === 403)) {
        return;
      }

      // log out the user if the /me call fails
      // the call probably fails because the user is already logged out
      // but the user may have a stuck session instead which should be cleaned up by the API
      // otherwise the user will have no way to login until the stuck session's ttl expires
      await dispatch(logout({ federatedLogout: false, silent: true }));
      if (!silent) {
        dispatch(
          notify({
            type: "error",
            message: i18n.t("Your login attempt failed, please try again later."),
          })
        );
      }
      console.error(err);
      return;
    }

    // immediately log out the user if they have ECS divisions but no EONS enabled divisions
    if (!user.divisions.length) {
      await dispatch(logout({ federatedLogout: false, silent: false }));
      dispatch(
        notify({
          type: "error",
          message: i18n.t("You are not part of an EONS enabled divisions"),
        })
      );
      return;
    }

    // set the user before doing anything with the storage
    // this is necessary because the storage is scoped per user
    dispatch(setUser(user));

    // set the active division id to the first eons enabled division if the stored id is invalid
    // this can happen if a new user logs into the same machine or if the user is removed from a division
    const { storage } = getState();
    const activeDivision =
      user.divisions.find(
        (division) => division.id === storage.activeDivisionId
      ) ||
      user.divisions.find((division) => division.isDefaultDivision) ||
      user.divisions[0];

    // this sets the active division id and fetches the relevant eons config
    await dispatch(
      setEonsConfig(activeDivision.id, activeDivision.companyId, {
        resetParams: false,
      })
    );
    await dispatch(setWalletConfig(activeDivision.id));
  };
}

export function login(loginData) {
  return async (dispatch) => {
    try {
      await userApi.login(loginData);
    } catch (err) {
      dispatch(
        notify({
          type: "error",
          message: "Invalid username or password!",
        })
      );
      console.error(err);
      return;
    }

    clearPersistedAuthMethod();
    await dispatch(getMe());
  };
}

export function logout({ federatedLogout = true, silent = false } = {}) {
  return async (dispatch) => {
    const azureLogoutUrl = federatedLogout ? buildAzureAdLogoutRedirectUrl() : null;

    try {
      await userApi.logout();
    } catch (err) {
      if (err.response?.status !== 401) {
        console.error(err);
      }
    }

    batch(() => {
      dispatch(resetStore());
      if (!silent) {
        dispatch(notify({ type: "info", message: i18n.t("You logged out") }));
      }
    });

    if (azureLogoutUrl) {
      window.location.assign(azureLogoutUrl);
    }
  };
}

function setUser(newUser) {
  return { type: SET_USER, newUser };
}

export function resetStore() {
  const preservedAuthMethod = preserveAuthMethodDuringStoreReset();

  // clear cookies and local storage
  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
  }

  // clear session storage
  sessionStorage.clear();

  // clear local storage (keep last SAML/OIDC auth method for post-logout login URL)
  localStorage.clear();
  restoreAuthMethodAfterStoreReset(preservedAuthMethod);

  return { type: RESET_STORE };
}

export function loginSSO(loginData) {
  return async (dispatch) => {
    try {
      await userApi.loginSSO(loginData);
    } catch (err) {
      dispatch(
        notify({
          type: "error",
          message: "Invalid username or password!",
        })
      );
      console.error(err);
      return;
    }
    persistOidcAuthMethod();
    await dispatch(getMe());
  };
}
